/* Picking a Venue while writing an Event — STORY-013.
 *
 * ── why the create panel is here rather than a link to Settings ─────────────
 * "Sending someone to a settings screen mid-task loses the Event they were
 * writing." So the panel takes a name and a zone, saves, and selects the
 * result — without leaving the Event.
 *
 * That is the opposite call from the timezone picker beside it, and the two are
 * worth reading together: a Venue is the team's own record and any Member may
 * add one, while enabling a zone is Owner-only workspace configuration. The
 * cost of the second hard stop was weighed and accepted in STORY-041; the cost
 * of this one was weighed and refused.
 *
 * ── a hidden Venue stays selected ──────────────────────────────────────────
 * Unavailable Venues are left out of the list EXCEPT the one already chosen,
 * which stays and is marked. Omitting it would blank the Venue the moment
 * somebody edited an unrelated field — a data loss with no error message.
 *
 * ── and so does one the list has not arrived for ────────────────────────────
 * The same failure, reached the other way: while the query is in flight, or
 * after it fails, the list is empty and the selected Venue has no <option> to
 * sit on — so the browser falls back to the first one and the control reads
 * "Nowhere yet" for an Event that has a Venue. The selection is therefore
 * seeded from the prop, not from the list, and a failed load says so instead
 * of looking like an empty workspace.
 */

import type { Venue } from '@eventuley/contracts';
import { zoneCity } from '@eventuley/i18n';
import { useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout, TextField } from '../../../shared/ui/index.js';
import { useCreateVenue, useVenues } from '../api/index.js';
import { TimezoneSelect } from './timezone-select.js';

export interface VenuePickerProps {
  readonly organisationId: string;
  /** The Venue currently on the Event, so it can stay selected even if hidden. */
  readonly selected: { id: string; name: string; available: boolean } | null;
  readonly enabledTimezones: readonly string[];
  readonly canManageTimezones: boolean;
  readonly onChange: (venueId: string | null) => void;
  readonly busy?: boolean;
}

export function VenuePicker({
  organisationId,
  selected,
  enabledTimezones,
  canManageTimezones,
  onChange,
  busy,
}: VenuePickerProps) {
  const t = useTranslate();
  const { data, error } = useVenues(organisationId);
  const create = useCreateVenue(organisationId);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  /* Set when a create turned out to be a reuse whose zone differs from the one
   * asked for. The existing record's zone stands, and saying so is the whole
   * point — otherwise the zone somebody typed is discarded without a word. */
  const [reused, setReused] = useState<{ name: string; timezone: string | null } | null>(null);

  const all: readonly Venue[] = data?.venues ?? [];

  /* Available ones, plus the selected one whatever its state. A team with
   * thirty Venues should not scroll past the twenty-five they no longer use —
   * but the one this Event is already at must never disappear. */
  const listed = all.filter((venue) => venue.available || venue.id === selected?.id);

  /* Seeded from the prop when the list has not arrived, or never will. Without
   * this the select has no option to sit on and silently reads "Nowhere yet". */
  const offered =
    selected && !listed.some((venue) => venue.id === selected.id)
      ? [{ id: selected.id, name: selected.name, available: selected.available }, ...listed]
      : listed;

  return (
    <div className="field">
      <label className="field">
        <span className="field__label">{t('venues.picker_label')}</span>
        <select
          className="field__control"
          value={selected?.id ?? ''}
          disabled={busy}
          onChange={(changed) =>
            onChange(changed.target.value === '' ? null : changed.target.value)
          }
        >
          {/* No Venue is a real answer, not a prompt: an idea usually has none,
              and its times read in the workspace's zone. */}
          <option value="">{t('venues.picker_none')}</option>
          {offered.map((venue) => (
            <option key={venue.id} value={venue.id}>
              {venue.available ? venue.name : t('venues.picker_hidden', { name: venue.name })}
            </option>
          ))}
        </select>
      </label>

      {/* A list that failed to load is said out loud. Silently offering only
          the current Venue would read as a workspace with one Venue in it. */}
      {error ? (
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('venues.picker_unavailable')}
        </Callout>
      ) : null}

      {reused ? (
        <Callout tone="info">
          {reused.timezone === null
            ? t('venues.reused_inherits', { name: reused.name })
            : t('venues.reused_zone', { name: reused.name, zone: zoneCity(reused.timezone) })}
        </Callout>
      ) : null}

      {adding ? (
        <Callout tone="info">
          <strong>{t('venues.add_heading')}</strong>
          {/* Not a form element: this sits INSIDE the Event's form, and a nested
              form is invalid HTML — the browser drops the inner one and the
              outer submit fires instead, saving the Event when somebody meant
              to save a Venue. */}
          <TextField
            label={t('venues.name_label')}
            value={name}
            onChange={(changed) => setName(changed.target.value)}
          />
          <TimezoneSelect
            label={t('venues.timezone_label')}
            value={timezone}
            enabled={enabledTimezones}
            canManage={canManageTimezones}
            onChange={setTimezone}
          />

          {create.error ? (
            <Callout tone="danger">
              {create.error instanceof NetworkError
                ? t('error.network')
                : create.error instanceof ApiError && create.error.code === 'validation_failed'
                  ? t('venues.name_required')
                  : t('error.unexpected')}
            </Callout>
          ) : null}

          <div className="btn-row">
            <Button
              busy={create.isPending}
              onClick={() =>
                create.mutate(
                  { name, ...(timezone === '' ? {} : { timezone }) },
                  {
                    onSuccess: (venue) => {
                      /* Created AND selected. Adding one and then having to
                         find it in the list is the half-finished version of
                         this feature. */
                      onChange(venue.id);
                      /* A name that already existed returns that record
                         untouched, so a zone typed alongside it does not
                         apply. Said rather than swallowed — the alternative
                         is somebody believing they set a zone they did not. */
                      setReused(
                        timezone !== '' && venue.timezone !== timezone
                          ? { name: venue.name, timezone: venue.timezone }
                          : null,
                      );
                      setName('');
                      setTimezone('');
                      setAdding(false);
                    },
                  },
                )
              }
            >
              {t('venues.add_submit')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                create.reset();
                setAdding(false);
              }}
            >
              {t('venues.add_cancel')}
            </Button>
          </div>
        </Callout>
      ) : (
        <Button
          variant="ghost"
          onClick={() => {
            create.reset();
            setReused(null);
            setAdding(true);
          }}
        >
          {t('venues.add_new')}
        </Button>
      )}
    </div>
  );
}
